// src/services/callLogService.js
// 通話紀錄 — API 服務層

import apiService from './apiService';

class CallLogService {
  /**
   * 取得通話紀錄列表（支援分頁、篩選、搜尋）
   * @param {Object} params - { page, limit, status, handled, date, search }
   * @returns {Promise<Object>} { success, data, pagination }
   */
  async getCallLogs(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.handled !== undefined && params.handled !== '') queryParams.append('handled', params.handled);
      if (params.date) queryParams.append('date', params.date);
      if (params.search) queryParams.append('search', params.search);
      if (params.direction && params.direction !== 'all') queryParams.append('direction', params.direction);
      if (params.campaignId) queryParams.append('campaignId', params.campaignId);

      const endpoint = `call-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiService.apiRequest('get', endpoint);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination
        };
      }

      return { success: false, message: response.data?.message || '取得通話紀錄失敗' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '取得通話紀錄請求失敗'
      };
    }
  }

  /**
   * 取得當日統計
   * @param {string} date - YYYY-MM-DD 格式
   * @returns {Promise<Object>} { success, data }
   */
  async getStats(date) {
    try {
      const endpoint = date ? `call-logs/stats?date=${date}` : 'call-logs/stats';
      const response = await apiService.apiRequest('get', endpoint);

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, message: response.data?.message || '取得統計失敗' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '取得統計請求失敗'
      };
    }
  }

  /**
   * 取得單筆通話詳情
   * @param {number} id
   * @returns {Promise<Object>} { success, data }
   */
  async getCallLogById(id) {
    try {
      const response = await apiService.apiRequest('get', `call-logs/${id}`);

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, message: response.data?.message || '取得通話詳情失敗' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '取得通話詳情請求失敗'
      };
    }
  }

  /**
   * 由 conversationId 反查通話紀錄（含 transcript）— 給訂位後台「通話紀錄」按鈕重用 call-logs 抽屜
   * @param {number} conversationId
   * @returns {Promise<Object>} { success, data }
   */
  async getCallLogByConversation(conversationId) {
    try {
      const response = await apiService.apiRequest('get', `call-logs/by-conversation/${conversationId}`);

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, message: response.data?.message || '取得通話紀錄失敗' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '此訂位沒有對應的通話紀錄'
      };
    }
  }

  /**
   * 取得公司的通話紀錄 UI 設定（標籤、回報選項等）
   * @param {number|string} companyId - 可選，代理商切換用
   * @returns {Promise<Object>} { success, data }
   */
  async getUiConfig(companyId = null) {
    try {
      const endpoint = companyId ? `call-logs/ui-config?companyId=${companyId}` : 'call-logs/ui-config';
      const response = await apiService.apiRequest('get', endpoint);

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, data: null };
    } catch (error) {
      return { success: false, data: null };
    }
  }

  /**
   * 更新通話紀錄（標記處理 / 備註）
   * @param {number} id
   * @param {Object} data - { handled, memo, memoTags }
   * @returns {Promise<Object>} { success, data }
   */
  async updateCallLog(id, data) {
    try {
      const response = await apiService.apiRequest('patch', `call-logs/${id}`, data);

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, message: response.data?.message || '更新通話紀錄失敗' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '更新通話紀錄請求失敗'
      };
    }
  }
}

const callLogService = new CallLogService();
export default callLogService;
